import { Injectable, signal, computed, effect } from '@angular/core';
import { OrgEntityColors } from '@newgenus/common';

/**
 * Entity type keys used throughout the organization dashboard.
 */
export type OrgEntityType = 'users' | 'hats' | 'positions' | 'securityGroups' | 'policies' | 'features' | 'permissions';

/**
 * Full resolved color map — always has a value for every entity type.
 */
export type ResolvedEntityColors = Required<OrgEntityColors>;

/**
 * Default colors for each entity type.
 * These match the hardcoded values previously used throughout the codebase.
 */
export const DEFAULT_ENTITY_COLORS: ResolvedEntityColors = {
    users: '#1976d2',
    hats: '#1093ad',
    positions: '#388e3c',
    securityGroups: '#f57c00',
    policies: '#00897b',
    features: '#d32f2f',
    permissions: '#5c6bc0',
};

/**
 * Mapping from entity type to CSS custom property name.
 */
const CSS_VAR_MAP: Record<OrgEntityType, string> = {
    users: '--entity-color-users',
    hats: '--entity-color-hats',
    positions: '--entity-color-positions',
    securityGroups: '--entity-color-security-groups',
    policies: '--entity-color-policies',
    features: '--entity-color-features',
    permissions: '--entity-color-permissions',
};

/**
 * Lightweight service that provides organization entity type colors
 * via Angular signals and CSS custom properties.
 *
 * Usage:
 *   - Inject `OrgEntityColorsService` and call `setColors(org.settings?.entityColors)`
 *     whenever the organization document updates.
 *   - In templates: `colorsService.colors().hats`
 *   - In CSS: `color: var(--entity-color-hats);`
 *   - For chip backgrounds: `colorsService.getBackground('hats')`
 *
 * The service automatically injects CSS custom properties on `document.documentElement`
 * so all stylesheets can reference them without any TS import.
 */
@Injectable({ providedIn: 'root' })
export class OrgEntityColorsService {

    /** Raw custom colors from org.settings.entityColors (may be partial or undefined) */
    private readonly customColors = signal<OrgEntityColors | undefined>(undefined);

    /** Fully resolved colors — always has a value for every entity type */
    public readonly colors = computed<ResolvedEntityColors>(() => {
        const custom = this.customColors();
        return {
            users: custom?.users || DEFAULT_ENTITY_COLORS.users,
            hats: custom?.hats || DEFAULT_ENTITY_COLORS.hats,
            positions: custom?.positions || DEFAULT_ENTITY_COLORS.positions,
            securityGroups: custom?.securityGroups || DEFAULT_ENTITY_COLORS.securityGroups,
            policies: custom?.policies || DEFAULT_ENTITY_COLORS.policies,
            features: custom?.features || DEFAULT_ENTITY_COLORS.features,
            permissions: custom?.permissions || DEFAULT_ENTITY_COLORS.permissions,
        };
    });

    constructor() {
        // Whenever colors change, inject CSS custom properties on the root element
        effect(() => {
            const resolved = this.colors();
            const root = document.documentElement;
            for (const [entityType, cssVar] of Object.entries(CSS_VAR_MAP)) {
                root.style.setProperty(cssVar, resolved[entityType as OrgEntityType]);
            }
        });
    }

    /**
     * Update with the organization's custom entity colors.
     * Call this whenever the org document updates.
     * Pass `undefined` to revert to defaults.
     */
    public setColors(entityColors: OrgEntityColors | undefined): void {
        this.customColors.set(entityColors);
    }

    /**
     * Get the resolved color for a specific entity type.
     * This reads the signal — fine for use in component code.
     */
    public getColor(type: OrgEntityType): string {
        return this.colors()[type];
    }

    /**
     * Get an rgba background color suitable for chips/badges (30% opacity default).
     */
    public getBackground(type: OrgEntityType, opacity = 0.30): string {
        return hexToRgba(this.colors()[type], opacity);
    }

    /**
     * Get a contrasting text color (white or dark) for readable text on the entity color background.
     * Uses WCAG relative luminance formula to determine optimal contrast.
     */
    public getContrastText(type: OrgEntityType): string {
        return getContrastTextColor(this.colors()[type]);
    }

    /**
     * Get readable text color for a chip/pill with a translucent entity-color background.
     * Blends the entity color at the effective opacity with the current theme surface,
     * then picks white or dark text for best contrast.
     */
    public getChipTextColor(type: OrgEntityType, opacity = 0.30): string {
        const isDark = document.body.classList.contains('dark-theme');
        const surface = isDark ? '#303030' : '#ffffff';
        const blended = blendWithSurface(this.colors()[type], surface, opacity);
        return getContrastTextColor(blended);
    }
}

/**
 * Convert a hex color to rgba string.
 */
function hexToRgba(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Calculate the optimal text color (white or dark) for a given background hex color.
 * Uses the WCAG 2.0 relative luminance formula for perceptual brightness.
 *
 * Steps:
 *  1. Convert hex to sRGB [0..1]
 *  2. Linearize each channel (gamma correction)
 *  3. Compute relative luminance: L = 0.2126*R + 0.7152*G + 0.0722*B
 *  4. Return white for dark backgrounds, dark for light backgrounds
 */
function getContrastTextColor(hex: string): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16) / 255;
    const g = parseInt(h.substring(2, 4), 16) / 255;
    const b = parseInt(h.substring(4, 6), 16) / 255;

    // Linearize sRGB channels
    const linearize = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const luminance = 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);

    // Threshold 0.179 gives ~4.5:1 contrast ratio with white
    return luminance > 0.179 ? '#1a1a1a' : '#e8e8e8';
}

/**
 * Blend a foreground hex color at a given opacity onto a surface hex color.
 * Returns the effective rendered color as a hex string.
 *
 * Formula per channel: result = fg * alpha + bg * (1 - alpha)
 */
function blendWithSurface(fgHex: string, bgHex: string, alpha: number): string {
    const fg = hexToRgb(fgHex);
    const bg = hexToRgb(bgHex);
    const blend = (f: number, b: number) => Math.round(f * alpha + b * (1 - alpha));
    const r = blend(fg.r, bg.r);
    const g = blend(fg.g, bg.g);
    const b = blend(fg.b, bg.b);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Parse a hex color string to RGB components.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const h = hex.replace('#', '');
    return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
    };
}
